import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { API_URL } from "@/api/application-api";
import { SaveEntityToLocationControlled } from "@/components/SaveEntityToLocation";
import { useAuth } from "@/context/auth-context";
import { EntityType } from "@/queries";
import { useRemixPrompt } from "@/queries/prompts";
import { type PendingRemix, readAndClearPendingRemix } from "@/utils/remix-cookie";
import { displayErrorToast } from "@/utils/toast";

/**
 * Normalises a raw API error body into a human-readable string.
 *
 * Handles three backend quirks:
 *  - `PromptLayerPermissionError` global handler omits `success` and may omit `message`.
 *  - Pydantic validation errors return `message` as an array of `{msg: string}` objects.
 *  - Some routes return a plain string body instead of JSON.
 */
function extractErrorMessage(data: unknown, fallback: string): string {
  if (!data || typeof data !== "object") {
    return fallback;
  }
  const obj = data as Record<string, unknown>;
  const raw = obj.message;

  if (Array.isArray(raw)) {
    const joined = raw
      .map((e: unknown) =>
        e && typeof e === "object" && "msg" in e
          ? String((e as Record<string, unknown>).msg)
          : JSON.stringify(e),
      )
      .join("; ");
    return joined || fallback;
  }

  if (typeof raw === "string" && raw.trim()) {
    return raw.trim();
  }

  return fallback;
}

export const RemixHandler = () => {
  const [pendingRemix, setPendingRemix] = useState<PendingRemix | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [defaultName, setDefaultName] = useState("");

  const authContext = useAuth();
  const remixMutation = useRemixPrompt();
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  useEffect(() => {
    const remix = readAndClearPendingRemix();
    if (!remix) {
      return;
    }

    const userToken = authContext?.userToken;

    // Fetch session config to get the prompt name for the dialog default.
    // The Authorization header is required for private deployments — omitting it
    // causes the backend to return 403 and the dialog never opens.
    fetch(`${API_URL}/api/sessions/${remix.share_token}`, {
      credentials: "include",
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
    })
      .then(async res => {
        // Parse the body regardless of status so we can surface a meaningful
        // message on non-2xx responses.
        const data: unknown = await res.json().catch(() => null);

        if (
          res.ok &&
          data &&
          typeof data === "object" &&
          (data as Record<string, unknown>).success &&
          (data as Record<string, unknown>).session
        ) {
          const session = (data as Record<string, unknown>).session as Record<string, unknown>;
          setDefaultName(typeof session.name === "string" ? session.name : "");
          setPendingRemix(remix);
          setDialogOpen(true);
        } else {
          displayErrorToast(
            extractErrorMessage(data, "This shared session is no longer available."),
          );
        }
      })
      .catch(() => {
        displayErrorToast("Could not load the shared session for remixing.");
      });
    // authContext is stable for the lifetime of the session — safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (name: string, folderId: number | null) => {
    if (!pendingRemix) {
      return;
    }

    if (!workspaceId) {
      displayErrorToast("Could not determine your workspace. Please try again.");
      return;
    }

    const result = await remixMutation.mutateAsync({
      share_token: pendingRemix.share_token,
      workspace_id: Number(workspaceId),
      name,
      folder_id: folderId,
    });

    navigate(`/workspace/${workspaceId}/prompt/${result.prompt_template.id}`);
  };

  if (!dialogOpen) {
    return null;
  }

  return (
    <SaveEntityToLocationControlled
      open={dialogOpen}
      onOpenChange={setDialogOpen}
      type={EntityType.PROMPT}
      onSave={handleSave}
      defaultName={defaultName}
      title="Remix Prompt"
      description="Choose a name and location for the remixed prompt."
      saveButtonText="Remix"
    />
  );
};
